import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useConfirmModal from 'app/hooks/useConfirmModal';

import {
  jsonToUrlParam,
  urlParamToJson
} from 'shared/utils/Utils'

import useDeeplink, {
  SEARCH_PARAM
} from 'app/hooks/useDeeplink';

import {
  switchAccount
} from 'app/actions/portalActions';

const useResourceParams = props => {
  const {
    resource,
    setResourceParams
  } = props;

  // preventing useUploadForm from wiping out search params
  if (!resource) {
    return
  }

  const resourceParams = useSelector(state => state.params[resource]);
  const account = useSelector(state => state.portalReducer.account);
  const dispatch = useDispatch();

  // get resource params from URL params on first load
  useEffect(() => {
    if (!setResourceParams) {
      return;
    }

    let urlParams = new window.URLSearchParams(window.location.search);
    let searchParam = urlParams.get(SEARCH_PARAM);

    if (searchParam) {
      let params = urlParamToJson(searchParam);
      dispatch(setResourceParams(params));

      // if an account_id is encoded and not active then deeplink into the account
      if (params.account_id && params.account_id !== account.id) {
        dispatch(switchAccount(params.account_id));
      }
    }
  }, []);

  // set URL params when the filter model is updated
  const history = useHistory();
  useEffect(() => {
    // get the current URL params
    let urlParams = new URLSearchParams(window.location.search);

    // if filter and sort models are empty,
    // then skip if first load or clear the URL param if they've been cleared
    // otherwise write the new filter and sort models to the URL param
    let empty = true;
    if (resourceParams) {
      const { filter_model, sort_model } = resourceParams;

      empty = !filter_model || (
        Object.keys(filter_model).length === 1 && !sort_model.length
      );
    }

    if (empty) {
      // assume this is first load if no search param is set
      let firstLoad = !urlParams.get(SEARCH_PARAM);

      if (firstLoad) {
        return;
      } else {
        // clear the search param from the URL bar (no filters are set)
        urlParams.delete(SEARCH_PARAM);
      }
    } else {
      // create the new search param.
      // set the account ID to enable deeplinking into the account
      let params = {
        ...resourceParams,
        account_id: account.id
      };

      let searchParam = jsonToUrlParam(params);

      // keep current URL params and only update the search param
      urlParams.set(SEARCH_PARAM, searchParam);
    }

    // update the URL without redirecting or refreshing the page
    history.replace({
      search: urlParams.toString()
    });

  }, [resourceParams]);

  return resourceParams;
}

const useResource = props => {
  const {
    getResource,
    createResource,
    updateResource,
    destroyResource,
    setResourceParams,
    resource,
    formatQuickEditParams,
    onCreate,
    onUpdate,
    disableShowOnCreate
  } = props;

  const deeplink = useDeeplink();

  const dispatch = useDispatch();
  const getRowData = params => {
    return dispatch(getResource(params));
  }

  const resourceParams = useResourceParams({
    resource,
    setResourceParams
  });

  const {
    showConfirmModal
  } = useConfirmModal();

  const dataGrid = useRef(null);
  const onDataGridLoad = grid => {
    dataGrid.current = grid;
  }

  const [state, setState] = useState({
    formIsOpen: false,
    currentRecord: null
  });

  const onAdd = () => setState({
    formIsOpen: true,
    currentRecord: null
  });

  const onFormClose = () => setState({
    formIsOpen: false,
    currentRecord: null
  });

  const onEdit = record => {
    setState({
      formIsOpen: true,
      currentRecord: record
    });
  }

  const onQuickAdd = async params => {
    if (formatQuickEditParams) {
      params = formatQuickEditParams(params);
    }
    return await createResource(params);
  }

  const onQuickEdit = async params => {
    if (formatQuickEditParams) {
      params = formatQuickEditParams(params);
    }
    return await updateResource(params.id, params);
  }

  const onFormSubmit = async (action, params) => {
    const { updateRow, refreshGrid } = dataGrid.current || {};

    let record = null;

    switch (action) {
      case 'update':
        if (updateResource) {
          record = await updateResource(state.currentRecord.id, params);
        }

        if (updateRow) {
          updateRow(record);
        }

        if (onUpdate) {
          onUpdate(record);
        }
        break;

      case 'create':
        if (createResource) {
          record = await createResource(params);
        }

        if (refreshGrid) {
          refreshGrid();
        }

        if (onCreate) {
          onCreate(record);
        }

      // todo: something like this
      //if (!disableShowOnCreate) {
      //  deeplink.openRecord(resource, record);
      //}
    }
  }

  // this works with both the grid and resource modal
  const onDestroy = async records => {
    const { refreshGrid } = dataGrid.current;

    // the records arg can be an array of objects, IDs or an ID.
    // in any case, it'll be converted to an array of IDs
    let ids;
    switch (typeof records) {
      case 'object':
        if (Array.isArray(records)) {
          ids = records.map(r => typeof r === 'number' ? r : r.id);
        } else {
          let record = records;
          ids = [record.id];
        }
        break;
      case 'number':
        let id = records;
        ids = [id];
        break;
      default:
        // see what happens
        ids = records;
    }

    showConfirmModal('Are you sure you want to delete these records?')
      .then(async () => {
        await destroyResource(ids);
        refreshGrid();
      });

    //onFormClose();
  }

  const getFilterModel = () => {
    const { filter_model } = resourceParams;
    return filter_model;
  }

  const refreshCells = () => {
    const { refreshCells } = dataGrid.current;
    refreshCells();
  }

  const refreshGrid = () => {
    const { refreshGrid } = dataGrid.current;
    refreshGrid();
  }

  const updateRow = record => {
    const { updateRow } = dataGrid.current;
    updateRow(record);

    // if this is the current record, then update the currentRecord state
    if (state.currentRecord && state.currentRecord.id === record.id) {
      setState(state => ({ ...state, currentRecord: record }));
    }
  }

  // show filters if set.
  // an empty filter model will still have an operatory key
  const showFilters = resourceParams && resourceParams.filter_model &&
    Object.keys(resourceParams.filter_model).length > 1;

  return {
    formIsOpen: state.formIsOpen,
    currentRecord: state.currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    refreshCells,
    refreshGrid,
    getRowData,
    updateRow,
    resource,
    getFilterModel
  }
}

export default useResource;